import { FunctionComponent } from "react";

import { SelectField } from "@/components/SelectField/SelectField";
import { TextArea } from "@/components/TextArea/TextArea";
import { ParallelAiMonitorFrequency } from "@/enum/parallel-ai-type.enum";

import { AiCompanyConfig, AiNewsConfig } from "../onboardingListTypes";
import { ConfigField, ConfigGrid, useConfigTranslation, useFieldError } from "./ConfigField";
import { ConfigFormProps } from "./configFormProps";

const FREQUENCIES = [ParallelAiMonitorFrequency.DAILY, ParallelAiMonitorFrequency.WEEKLY];

/** PARALLEL_AI / SEARCH — one plain-language description of the companies to find. */
export const AiCompanyForm: FunctionComponent<ConfigFormProps<AiCompanyConfig>> = ({
	value,
	errors,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	return (
		<ConfigGrid>
			<ConfigField full required error={errorFor("aiQuery")} label={ts("companyDescription")}>
				<TextArea
					error={Boolean(errors.aiQuery)}
					handleChange={(event) => onChange({ ...value, query: event.target.value })}
					name="aiCompanyQuery"
					placeholder={ts("companyDescriptionPlaceholder")}
					showError={Boolean(errors.aiQuery)}
					value={value.query}
				/>
			</ConfigField>
		</ConfigGrid>
	);
};

/** PARALLEL_AI / MONITOR — the same description, re-checked on a cadence. */
export const AiNewsForm: FunctionComponent<ConfigFormProps<AiNewsConfig>> = ({
	value,
	errors,
	onChange,
}) => {
	const ts = useConfigTranslation();
	const errorFor = useFieldError(errors);

	return (
		<ConfigGrid>
			<ConfigField full required error={errorFor("aiQuery")} label={ts("newsQuery")}>
				<TextArea
					error={Boolean(errors.aiQuery)}
					handleChange={(event) => onChange({ ...value, query: event.target.value })}
					name="aiNewsQuery"
					placeholder={ts("newsQueryPlaceholder")}
					showError={Boolean(errors.aiQuery)}
					value={value.query}
				/>
			</ConfigField>

			<ConfigField label={ts("frequency")}>
				<SelectField
					hideEmptyValue
					handleSelect={(frequency) =>
						onChange({ ...value, frequency: frequency as ParallelAiMonitorFrequency })
					}
					options={FREQUENCIES.map((frequency) => ({
						title: ts(
							frequency === ParallelAiMonitorFrequency.DAILY ? "frequencyDaily" : "frequencyWeekly"
						),
						value: frequency,
					}))}
					selectedOption={value.frequency}
					testId="onboarding-aiNews-frequency"
				/>
			</ConfigField>
		</ConfigGrid>
	);
};
